import type {
  LockerverseCatalog,
  LockerverseCheckoutClient,
  LockerverseCheckoutEstimate,
  LockerverseCheckoutItem,
  LockerversePaymentCompletion,
  LockerversePaymentResult,
  LockerversePreparedCheckout,
} from "@lockerverse/sdk/checkout";
import { LockerverseSdkError } from "@lockerverse/sdk/checkout";
import type { Stripe, StripeElements } from "@stripe/stripe-js";
import { useCallback, useEffect, useRef, useState } from "react";
import type { CheckoutFieldValues } from "./checkout-fields.tsx";
import { getPhoneServerError } from "./contact-validation.ts";
import { formatMoney } from "./format-money.ts";
import { notifyHostCallback } from "./host-callback.ts";
import {
  type PaymentAttemptOutcome,
  recoverPaymentAttempt,
} from "./payment-attempt.ts";
import {
  getPaymentError,
  getPaymentUiState,
  isPaymentBusy,
  type PaymentFormAction,
  type PaymentFormState,
  type PaymentUiState,
} from "./payment-form-state.ts";
import {
  buildQuoteRequest,
  getLocalQuotePreview,
  getQuoteRefreshFailure,
  getQuoteRefreshSuccess,
} from "./payment-quote.ts";
import {
  type CheckoutAddresses,
  type CompletePaymentResult,
  completePayment,
} from "./payment-submission.ts";
import { validateContact } from "./payment-validation.ts";
import type { LockerversePaymentProps } from "./types.ts";

function notifyPaymentRecovery(
  callback: LockerversePaymentProps["onPaymentRecoveryChange"],
  recovery: Parameters<
    NonNullable<LockerversePaymentProps["onPaymentRecoveryChange"]>
  >[0]
) {
  notifyHostCallback(callback, recovery);
}

function notifyPaymentUncertain(
  callback: LockerversePaymentProps["onPaymentUncertain"],
  payment: Parameters<
    NonNullable<LockerversePaymentProps["onPaymentUncertain"]>
  >[0]
) {
  notifyHostCallback(callback, payment);
}

function createPaymentCompletion(
  payment: LockerversePaymentResult,
  context: Pick<LockerversePaymentCompletion, "email" | "lineItems">
): LockerversePaymentCompletion {
  return { ...payment, ...context };
}

function mustWaitForStripe(
  outcome: PaymentAttemptOutcome,
  stripe: Stripe | null,
  stripeLoadFailed: boolean
) {
  return (
    outcome.kind === "unresolved" &&
    outcome.reason === "action_required" &&
    !stripe &&
    !stripeLoadFailed
  );
}

function isSoldOutError<ErrorValue>(error: ErrorValue) {
  return (
    error instanceof LockerverseSdkError &&
    error.serverCode === "PAYMENT_WIDGET_SALES_LIMIT_REACHED"
  );
}

export type PaymentFormControllerOptions = {
  catalog: LockerverseCatalog;
  checkoutItems: LockerverseCheckoutItem[];
  client: LockerverseCheckoutClient;
  elements: StripeElements | null;
  email: string;
  fieldValues: CheckoutFieldValues;
  metadata: Record<string, string> | undefined;
  onPaymentComplete: LockerversePaymentProps["onPaymentComplete"];
  onResumePaymentFailed: () => void;
  onPaymentRecoveryChange: LockerversePaymentProps["onPaymentRecoveryChange"];
  onPaymentUncertain: LockerversePaymentProps["onPaymentUncertain"];
  onQuoteChange: (quote: LockerversePreparedCheckout, notice?: string) => void;
  onStateChange: (state: PaymentUiState) => void;
  quote: LockerverseCheckoutEstimate | LockerversePreparedCheckout;
  resumePayment: LockerversePaymentProps["resumePayment"];
  stripe: Stripe | null;
  stripeConnectedAccountId: string | null;
  stripeLoadFailed: boolean;
  paymentState: PaymentFormState;
  updateState: (action: PaymentFormAction, notifyHost?: boolean) => void;
};

type SubmitPayment = {
  addresses: CheckoutAddresses;
  phone: string;
};

function applyQuoteRefreshSuccess({
  nextQuote,
  onQuoteChange,
  setDisplayedTotalCents,
  setPromoError,
  updateState,
  validatePromo,
}: {
  nextQuote: LockerversePreparedCheckout;
  onQuoteChange: (quote: LockerversePreparedCheckout, notice?: string) => void;
  setDisplayedTotalCents: (total: number) => void;
  setPromoError: (error: string | undefined) => void;
  updateState: (action: PaymentFormAction) => void;
  validatePromo: boolean;
}) {
  const result = getQuoteRefreshSuccess(nextQuote, validatePromo);
  updateState({ type: "quote_refresh_succeeded" });
  setDisplayedTotalCents(result.displayedTotalCents);
  onQuoteChange(nextQuote);
  if (validatePromo) {
    setPromoError(result.promoError);
  }
}

function applyQuoteRefreshFailure({
  error,
  setPromoError,
  updateState,
  validatePromo,
}: {
  error: unknown;
  setPromoError: (error: string | undefined) => void;
  updateState: (action: PaymentFormAction) => void;
  validatePromo: boolean;
}) {
  const result = getQuoteRefreshFailure(error, validatePromo);
  if (validatePromo) {
    setPromoError(result.promoError);
  }
  updateState(
    result.paymentError === undefined
      ? { type: "quote_refresh_failed" }
      : { error: result.paymentError, type: "quote_refresh_failed" }
  );
}

function applyPaymentOutcome({
  onPaymentComplete,
  onPaymentRecoveryChange,
  onPaymentUncertain,
  onQuoteChange,
  result,
  setDisplayedTotalCents,
  dispatchState,
}: {
  onPaymentComplete: LockerversePaymentProps["onPaymentComplete"];
  onPaymentRecoveryChange: LockerversePaymentProps["onPaymentRecoveryChange"];
  onPaymentUncertain: LockerversePaymentProps["onPaymentUncertain"];
  onQuoteChange: (quote: LockerversePreparedCheckout, notice?: string) => void;
  result: CompletePaymentResult;
  setDisplayedTotalCents: (total: number) => void;
  dispatchState: (action: PaymentFormAction) => void;
}) {
  if (result.kind === "quote_changed") {
    const notice = `The total changed to ${formatMoney(result.quote.totalCents)}. Review it and pay again.`;
    onQuoteChange(result.quote, notice);
    setDisplayedTotalCents(result.quote.totalCents);
    dispatchState({ type: "quote_changed" });
    return;
  }
  if (result.kind === "payment_failed") {
    notifyPaymentRecovery(onPaymentRecoveryChange, null);
    dispatchState({ error: result.message, type: "retryable_failure" });
    return;
  }
  if (result.kind === "payment_unresolved") {
    dispatchState({
      message: "Payment is still processing. Do not submit it again.",
      type: "unresolved",
    });
    notifyPaymentUncertain(onPaymentUncertain, {
      paymentReference: result.paymentReference,
      reason: result.reason,
    });
    return;
  }
  onQuoteChange(result.quote);
  notifyPaymentRecovery(onPaymentRecoveryChange, null);
  dispatchState({
    message: result.payment.paymentRequired
      ? "Payment completed successfully."
      : "Order confirmed. No payment was required.",
    type: "succeeded",
  });
  notifyHostCallback(
    onPaymentComplete,
    createPaymentCompletion(result.payment, {
      email: result.email,
      lineItems: result.quote.lineItems,
    })
  );
}

export function usePaymentFormController(
  options: PaymentFormControllerOptions
) {
  const {
    catalog,
    checkoutItems,
    client,
    elements,
    email,
    fieldValues,
    metadata,
    onPaymentComplete,
    onResumePaymentFailed,
    onPaymentRecoveryChange,
    onPaymentUncertain,
    onQuoteChange,
    onStateChange,
    quote,
    resumePayment,
    stripe,
    stripeConnectedAccountId,
    stripeLoadFailed,
    paymentState,
    updateState,
  } = options;
  const [displayedTotalCents, setDisplayedTotalCents] = useState(
    quote.totalCents
  );
  const [promoError, setPromoError] = useState<string>();
  const recoveryStartedRef = useRef(false);
  const quoteRequestRef = useRef(0);
  const attemptGenerationRef = useRef(0);
  const dispatchState = useCallback(
    (action: PaymentFormAction) => updateState(action, true),
    [updateState]
  );
  const startPaymentAttempt = useCallback(() => {
    const generation = attemptGenerationRef.current + 1;
    attemptGenerationRef.current = generation;
    return () => attemptGenerationRef.current === generation;
  }, []);

  useEffect(
    () => () => {
      attemptGenerationRef.current += 1;
      quoteRequestRef.current += 1;
    },
    []
  );

  const { parsedTip, totalCents: previewTotalCents } = getLocalQuotePreview(
    catalog,
    fieldValues,
    quote
  );

  useEffect(() => {
    if (previewTotalCents === null) {
      return;
    }
    setDisplayedTotalCents(previewTotalCents);
    if (previewTotalCents > 0) {
      elements?.update({ amount: previewTotalCents });
    }
  }, [
    catalog.pricingPolicy.serviceFeeRateBasisPoints,
    catalog.widget.fields.serviceFee,
    elements,
    fieldValues.includeServiceFee,
    previewTotalCents,
    quote.serviceFeeCents,
    quote.tipCents,
    quote.totalCents,
    quote.subtotalCents,
  ]);

  useEffect(() => {
    if (
      !resumePayment ||
      recoveryStartedRef.current ||
      paymentState.status !== "recovering"
    ) {
      return;
    }
    recoveryStartedRef.current = true;
    const isAttemptActive = startPaymentAttempt();
    onStateChange(getPaymentUiState(paymentState));

    recoverPaymentAttempt(client, resumePayment.paymentReference, stripe).then(
      (outcome) => {
        if (!isAttemptActive()) {
          return;
        }
        if (mustWaitForStripe(outcome, stripe, stripeLoadFailed)) {
          return;
        }
        if (outcome.kind === "failed") {
          const message =
            "The previous payment failed. Check your details and try again.";
          notifyPaymentRecovery(onPaymentRecoveryChange, null);
          onResumePaymentFailed();
          dispatchState({
            error: message,
            type: "recovery_failed",
          });
          return;
        }
        if (outcome.kind === "unresolved") {
          dispatchState({
            message: "Payment is still processing. Do not submit it again.",
            type: "unresolved",
          });
          notifyPaymentUncertain(onPaymentUncertain, {
            paymentReference: outcome.paymentReference,
            reason: outcome.reason,
          });
          return;
        }

        notifyPaymentRecovery(onPaymentRecoveryChange, null);
        dispatchState({
          message: outcome.payment.paymentRequired
            ? "Payment completed successfully."
            : "Order confirmed. No payment was required.",
          type: "succeeded",
        });
        notifyHostCallback(
          onPaymentComplete,
          createPaymentCompletion(outcome.payment, {
            email: null,
            lineItems: null,
          })
        );
      }
    );
    return () => {
      if (isAttemptActive()) {
        attemptGenerationRef.current += 1;
      }
      recoveryStartedRef.current = false;
    };
  }, [
    client,
    onPaymentComplete,
    onResumePaymentFailed,
    onPaymentRecoveryChange,
    onPaymentUncertain,
    onStateChange,
    resumePayment,
    stripe,
    stripeLoadFailed,
    startPaymentAttempt,
    dispatchState,
    paymentState,
  ]);

  const fieldChanged = useCallback(
    (clearPromo = false, preserveQuoteRequest = false) => {
      if (!preserveQuoteRequest) {
        quoteRequestRef.current += 1;
      }
      if (clearPromo) {
        setPromoError(undefined);
      }
      updateState({ type: "field_changed" });
    },
    [updateState]
  );

  async function refreshAuthoritativeTotal(
    allowMissingEmail = false,
    validatePromo = false
  ) {
    const normalizedEmail = email.trim().toLowerCase();
    const emailValidation = validateContact(
      { email: normalizedEmail, phone: "" },
      false
    );
    if (!(parsedTip.success && (emailValidation.value || allowMissingEmail))) {
      return;
    }

    quoteRequestRef.current += 1;
    const requestId = quoteRequestRef.current;
    updateState({ type: "quote_refresh_started" });
    try {
      const nextQuote = await client.calculateTotal({
        ...buildQuoteRequest(
          catalog,
          fieldValues,
          emailValidation.value?.email ?? "",
          parsedTip.cents
        ),
        catalog,
        items: checkoutItems,
      });
      if (requestId !== quoteRequestRef.current) {
        return;
      }
      applyQuoteRefreshSuccess({
        nextQuote,
        onQuoteChange,
        setDisplayedTotalCents,
        setPromoError,
        updateState,
        validatePromo,
      });
    } catch (error) {
      if (requestId !== quoteRequestRef.current) {
        return;
      }
      if (isSoldOutError(error)) {
        updateState({ type: "sold_out" });
        return;
      }
      applyQuoteRefreshFailure({
        error,
        setPromoError,
        updateState,
        validatePromo,
      });
    }
  }

  async function applyPromo() {
    setPromoError(undefined);
    await refreshAuthoritativeTotal(true, true);
  }

  async function submit({ addresses, phone }: SubmitPayment) {
    dispatchState({ type: "submission_started" });
    const isAttemptActive = startPaymentAttempt();
    try {
      const result = await completePayment({
        addresses,
        catalog,
        checkoutItems,
        client,
        elements,
        email,
        fieldValues,
        isAttemptActive,
        locallyPreviewedTotalCents: displayedTotalCents,
        metadata,
        onPaymentRecoveryChange,
        phone,
        quote,
        stripe,
        stripeConnectedAccountId,
      });
      if (!isAttemptActive()) {
        return;
      }
      applyPaymentOutcome({
        dispatchState,
        onPaymentComplete,
        onPaymentRecoveryChange,
        onPaymentUncertain,
        onQuoteChange,
        result,
        setDisplayedTotalCents,
      });
    } catch (error) {
      if (!isAttemptActive()) {
        return;
      }
      notifyPaymentRecovery(onPaymentRecoveryChange, null);
      if (isSoldOutError(error)) {
        dispatchState({ type: "sold_out" });
        return;
      }
      const phoneError =
        error instanceof LockerverseSdkError
          ? getPhoneServerError(error.serverCode)
          : undefined;
      if (phoneError) {
        dispatchState({ type: "submission_validation_failed" });
        return { phoneError };
      }
      dispatchState({
        error:
          error instanceof Error
            ? error.message
            : "Payment could not be completed.",
        type: "retryable_failure",
      });
    }
  }

  const terminalMessage =
    paymentState.status === "succeeded" || paymentState.status === "unresolved"
      ? paymentState.message
      : undefined;
  return {
    commands: {
      applyPromo,
      fieldChanged,
      refreshAuthoritativeTotal,
      reportStripeLoadError: (message: string) =>
        dispatchState({ error: message, type: "retryable_failure" }),
      reportValidationFailure: () =>
        dispatchState({ type: "validation_failed" }),
      submit,
    },
    state: {
      displayedTotalCents,
      isRecovering: paymentState.status === "recovering",
      isRefreshingQuote: paymentState.status === "refreshing_quote",
      isSubmitting: isPaymentBusy(paymentState),
      parsedTip,
      paymentError: getPaymentError(paymentState),
      promoError,
      terminalMessage,
    },
  };
}
